import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ClientSearchSelect } from "./ClientSearchSelect";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { Upload, Camera, FileText, X } from "lucide-react";

const ActionsContext = createContext(null);
export const useActions = () => useContext(ActionsContext);

const CURRENCIES = ["TRY", "USD", "EUR", "GBP"];
const CATEGORIES = ["Harç", "Tebligat", "Bilirkişi", "Keşif", "Noter", "Tercüme", "TÜRKPATENT", "Ulaşım", "Konaklama", "Kargo", "Vergi", "Resmî Kurum Ödemesi", "Diğer"];
const PAYMENT_METHODS = ["Havale/EFT", "Nakit", "Kredi Kartı", "Çek", "Diğer"];
const TITLES = {
  client: "Yeni Müvekkil", advance: "Avans Ekle", expense: "Masraf Ekle",
  collection: "Tahsilat Ekle", fee: "Vekâlet Ücreti Ekle", document: "Belge Ekle", note: "Not Ekle",
};
const today = () => new Date().toISOString().slice(0, 10);

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
      {children}
    </div>
  );
}

function ClientPicker({ value, onChange, clients }) {
  return (
    <Field label="Müvekkil">
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid="dialog-client-select"><SelectValue placeholder="Müvekkil seçin" /></SelectTrigger>
        <SelectContent>
          {clients.map((c) => (
            <SelectItem key={c.id} value={c.id}>{c.client_no} — {c.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

function CurrencySelect({ value, onChange }) {
  return (
    <Field label="Para Birimi">
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid="dialog-currency-select"><SelectValue /></SelectTrigger>
        <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
      </Select>
    </Field>
  );
}

export function ActionsProvider({ children }) {
  const [state, setState] = useState({ open: false, type: null, clientId: null, onDone: null });
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [editId, setEditId] = useState(null);
  const [openFees, setOpenFees] = useState([]);

  const open = useCallback((type, opts = {}) => {
    const base = { currency: "TRY", date: today(), due_date: today(), category: "Diğer", client_type: "individual", min_advance_limit: "0", doc_type: "DIGER" };
    if (type === "client" && opts.editClient) {
      const ec = opts.editClient;
      setForm({ ...base, ...ec, min_advance_limit: String(ec.min_advance_limit ?? "0"), client_id: "" });
      setEditId(ec.id);
    } else {
      setForm({ ...base, client_id: opts.clientId || "" });
      setEditId(null);
    }
    setSelectedClient(null);
    setFile(null);
    setState({ open: true, type, clientId: opts.clientId || null, onDone: opts.onDone || null });
  }, []);

  const close = () => setState((s) => ({ ...s, open: false }));

  useEffect(() => {
    if (state.open && !state.clientId && state.type !== "client") {
      api.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
    }
  }, [state.open, state.clientId, state.type]);

  useEffect(() => {
    if (state.open && state.type === "collection" && form.client_id) {
      api.get(`/fees?client_id=${form.client_id}`)
        .then(({ data }) => setOpenFees(data.filter((f) => Number(f.remaining) > 0)))
        .catch(() => setOpenFees([]));
    } else {
      setOpenFees([]);
    }
  }, [state.open, state.type, form.client_id]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async () => {
    const { type } = state;
    const needsClient = type && type !== "client";
    if (needsClient && !form.client_id) { toast.error("Lütfen müvekkil seçin"); return; }
    try {
      setBusy(true);
      if (type === "client") {
        const payload = {
          client_type: form.client_type, name: form.name, tax_or_id_no: form.tax_or_id_no || "",
          phone: form.phone || "", email: form.email || "", address: form.address || "",
          contact_person: form.contact_person || "", description: form.description || "",
          min_advance_limit: form.min_advance_limit || "0", is_active: form.is_active ?? true,
        };
        if (editId) { await api.put(`/clients/${editId}`, payload); toast.success("Müvekkil güncellendi"); }
        else { await api.post("/clients", payload); toast.success("Müvekkil oluşturuldu"); }
      } else if (type === "advance") {
        await api.post("/advances", { client_id: form.client_id, date: form.date, amount: form.amount, currency: form.currency, payment_method: form.payment_method || "", description: form.description || "" });
        toast.success("Avans kaydedildi");
      } else if (type === "expense") {
        await api.post("/expenses", { client_id: form.client_id, date: form.date, category: form.category, amount: form.amount, currency: form.currency, description: form.description || "" });
        toast.success("Masraf kaydedildi");
      } else if (type === "collection") {
        const { data } = await api.post("/collections", { client_id: form.client_id, date: form.date, amount: form.amount, currency: form.currency, payment_method: form.payment_method || "", description: form.description || "", fee_id: form.fee_id || null });
        toast.success("Tahsilat kaydedildi");
        if (data && data.warning) toast.warning(data.warning);
      } else if (type === "fee") {
        await api.post("/fees", { client_id: form.client_id, description: form.description || "", amount: form.amount, currency: form.currency, due_date: form.due_date });
        toast.success("Vekâlet ücreti kaydedildi");
      } else if (type === "note") {
        await api.post("/notes", { client_id: form.client_id, text: form.text });
        toast.success("Not eklendi");
      } else if (type === "document") {
        if (!file) { toast.error("Lütfen dosya seçin"); setBusy(false); return; }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("client_id", form.client_id);
        fd.append("doc_type", form.doc_type);
        const { data } = await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data.storage === "drive") toast.success("Belge Google Drive'a yüklendi");
        else toast.success("Belge yüklendi (yerel depolama)");
      }
      close();
      state.onDone && state.onDone();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "İşlem başarısız");
    } finally {
      setBusy(false);
    }
  };

  const t = state.type;
  const needClient = t && t !== "client";
  const clientField = state.clientId ? null : (
    <Field label="Müvekkil">
      <ClientSearchSelect selected={selectedClient}
        onSelect={(c) => { setSelectedClient(c); setForm((f) => ({ ...f, client_id: c.id })); }} />
    </Field>
  );

  return (
    <ActionsContext.Provider value={{ open }}>
      {children}
      <Dialog open={state.open} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-md rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="action-dialog">
          <DialogHeader><DialogTitle className="font-heading text-navy-900">{TITLES[t] || ""}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1">
            {needClient && clientField}

            {t === "client" && (
              <>
                <Field label="Müvekkil Türü">
                  <Select value={form.client_type} onValueChange={set("client_type")}>
                    <SelectTrigger data-testid="client-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Gerçek Kişi</SelectItem>
                      <SelectItem value="corporate">Tüzel Kişi</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={form.client_type === "corporate" ? "Ticaret Unvanı" : "Ad Soyad"}>
                  <Input data-testid="client-name-input" value={form.name || ""} onChange={set("name")} placeholder="Örn. ABC A.Ş." />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label={form.client_type === "corporate" ? "Vergi No" : "T.C. Kimlik No"}>
                    <Input value={form.tax_or_id_no || ""} onChange={set("tax_or_id_no")} />
                  </Field>
                  <Field label="Telefon"><Input value={form.phone || ""} onChange={set("phone")} /></Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="E-posta"><Input value={form.email || ""} onChange={set("email")} /></Field>
                  <Field label="Yetkili Kişi"><Input value={form.contact_person || ""} onChange={set("contact_person")} /></Field>
                </div>
                <Field label="Adres"><Input value={form.address || ""} onChange={set("address")} /></Field>
                <Field label="Minimum Avans Limiti (TRY)">
                  <Input data-testid="client-minlimit-input" type="number" value={form.min_advance_limit} onChange={set("min_advance_limit")} />
                </Field>
                <Field label="Açıklama"><Textarea value={form.description || ""} onChange={set("description")} rows={2} /></Field>
              </>
            )}

            {(t === "advance" || t === "expense" || t === "collection") && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Tarih"><Input type="date" value={form.date} onChange={set("date")} data-testid="dialog-date-input" /></Field>
                  <CurrencySelect value={form.currency} onChange={set("currency")} />
                </div>
                {t === "expense" && (
                  <Field label="Masraf Kategorisi">
                    <Select value={form.category} onValueChange={set("category")}>
                      <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                )}
                <Field label="Tutar"><Input data-testid="dialog-amount-input" type="number" step="0.01" value={form.amount || ""} onChange={set("amount")} placeholder="0,00" /></Field>
                {(t === "advance" || t === "collection") && (
                  <Field label="Ödeme Yöntemi">
                    <Select value={form.payment_method || ""} onValueChange={set("payment_method")}>
                      <SelectTrigger><SelectValue placeholder="Seçin" /></SelectTrigger>
                      <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                )}
                {t === "collection" && openFees.filter((f) => f.currency === form.currency).length > 0 && (
                  <Field label="Vekâlet Ücretine Bağla (opsiyonel)">
                    <Select value={form.fee_id || "NONE"} onValueChange={(v) => set("fee_id")(v === "NONE" ? "" : v)}>
                      <SelectTrigger data-testid="collection-fee-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NONE">Bağlama</SelectItem>
                        {openFees.filter((f) => f.currency === form.currency).map((f) => (
                          <SelectItem key={f.id} value={f.id}>
                            {(f.description || "Vekâlet ücreti")} — Kalan {new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2 }).format(Number(f.remaining))} {f.currency}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                )}
                <Field label="Açıklama"><Textarea value={form.description || ""} onChange={set("description")} rows={2} /></Field>
              </>
            )}

            {t === "fee" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Vade Tarihi"><Input type="date" value={form.due_date} onChange={set("due_date")} data-testid="fee-due-input" /></Field>
                  <CurrencySelect value={form.currency} onChange={set("currency")} />
                </div>
                <Field label="Tutar"><Input data-testid="dialog-amount-input" type="number" step="0.01" value={form.amount || ""} onChange={set("amount")} /></Field>
                <Field label="Açıklama"><Textarea value={form.description || ""} onChange={set("description")} rows={2} /></Field>
              </>
            )}

            {t === "note" && (
              <Field label="Not"><Textarea data-testid="note-text-input" value={form.text || ""} onChange={set("text")} rows={4} /></Field>
            )}

            {t === "document" && (
              <>
                <Field label="Belge Türü">
                  <Select value={form.doc_type} onValueChange={set("doc_type")}>
                    <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AVANS">Avans Belgesi</SelectItem>
                      <SelectItem value="MASRAF">Masraf Belgesi</SelectItem>
                      <SelectItem value="VEKALET">Vekâlet Ücreti Belgesi</SelectItem>
                      <SelectItem value="TAHSILAT">Tahsilat Belgesi</SelectItem>
                      <SelectItem value="DIGER">Diğer</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <div className="grid grid-cols-3 gap-2">
                  <label className="flex flex-col items-center gap-1 border border-slate-200 rounded-xl py-3 cursor-pointer hover:bg-slate-50 transition-colors">
                    <Camera className="w-5 h-5 text-navy-900" /><span className="text-[11px]">Kamera</span>
                    <input data-testid="doc-camera-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
                  </label>
                  <label className="flex flex-col items-center gap-1 border border-slate-200 rounded-xl py-3 cursor-pointer hover:bg-slate-50 transition-colors">
                    <Upload className="w-5 h-5 text-navy-900" /><span className="text-[11px]">Galeri</span>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
                  </label>
                  <label className="flex flex-col items-center gap-1 border border-slate-200 rounded-xl py-3 cursor-pointer hover:bg-slate-50 transition-colors">
                    <FileText className="w-5 h-5 text-navy-900" /><span className="text-[11px]">Dosya</span>
                    <input data-testid="doc-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
                  </label>
                </div>
                {file && (
                  <div className="flex items-center justify-between bg-slate-50 rounded-xl px-3 py-2 text-sm">
                    <span className="truncate">{file.name}</span>
                    <button onClick={() => setFile(null)}><X className="w-4 h-4 text-slate-400" /></button>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={close} className="rounded-xl">İptal</Button>
            <Button data-testid="dialog-submit-btn" onClick={submit} disabled={busy}
              className="rounded-xl bg-navy-900 hover:bg-navy-800 text-white">
              {busy ? "Kaydediliyor…" : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ActionsContext.Provider>
  );
}
